import type { IIssueInProgressDailyTrendRow } from '@skystone/shared';
import { bucketDailyWipToWeekly, sumDailyTotalsByType } from './weekly-bucketing';

/**
 * Pure aggregation helpers for Metric View's summary cards. Kept separate from
 * data-fetching code so they are trivially unit-testable.
 */

export type IssueTypeKey = 'Story' | 'Bug' | 'Task';
export const ISSUE_TYPES: ReadonlyArray<IssueTypeKey> = ['Story', 'Bug', 'Task'];

export interface IWeeklyCounts {
  week: string;
  Story: number;
  Bug: number;
  Task: number;
}

export interface IPerTypeNumber {
  Story: number;
  Bug: number;
  Task: number;
}

/**
 * Average of the last `windowWeeks` entries of the series, per issue type.
 * If the series is shorter than `windowWeeks`, averages whatever is present.
 * Returns zero when series is empty.
 */
export function rollingWeeklyAverage(
  series: IWeeklyCounts[],
  windowWeeks: number,
): IPerTypeNumber {
  if (series.length === 0) return { Story: 0, Bug: 0, Task: 0 };
  const window = series.slice(-Math.max(1, windowWeeks));
  const sum = window.reduce(
    (acc, w) => ({
      Story: acc.Story + w.Story,
      Bug: acc.Bug + w.Bug,
      Task: acc.Task + w.Task,
    }),
    { Story: 0, Bug: 0, Task: 0 },
  );
  return {
    Story: Math.round(sum.Story / window.length),
    Bug: Math.round(sum.Bug / window.length),
    Task: Math.round(sum.Task / window.length),
  };
}

export interface IWowDelta {
  deltaValue: number | null;
  direction: 'up' | 'down' | 'flat' | null;
}

/**
 * Week-over-week delta for the most recent complete week vs the one before.
 * Returns {null, null} when the series has fewer than 2 entries.
 * "flat" when the absolute delta is zero.
 */
export function wowDelta(current: number, previous: number | null): IWowDelta {
  if (previous === null) return { deltaValue: null, direction: null };
  const diff = current - previous;
  if (diff === 0) return { deltaValue: 0, direction: 'flat' };
  return { deltaValue: diff, direction: diff > 0 ? 'up' : 'down' };
}

/**
 * For each issue type, the WoW delta computed from the final two entries of
 * the series. When the series has < 2 entries, returns {null, null} per type.
 */
export function wowDeltasByType(series: IWeeklyCounts[]): Record<IssueTypeKey, IWowDelta> {
  if (series.length < 2) {
    return {
      Story: { deltaValue: null, direction: null },
      Bug: { deltaValue: null, direction: null },
      Task: { deltaValue: null, direction: null },
    };
  }
  const last = series.at(-1)!;
  const prev = series.at(-2)!;
  return {
    Story: wowDelta(last.Story, prev.Story),
    Bug: wowDelta(last.Bug, prev.Bug),
    Task: wowDelta(last.Task, prev.Task),
  };
}

/**
 * Monthly average across the full daily series, per issue type. Groups rows by
 * year-month of `snapshotDate`, averages within each month, then averages the
 * monthly values. Returns zeros when no rows.
 *
 * Per PRODOPSAI-422: the input may contain one row per (date, type, team).
 * Per-team rows are first SUMMED across teams to produce one daily total per
 * (date, type); only then are daily totals averaged within a month and across
 * months. Without that collapse step, averaging across rows would under-count
 * by a factor of N (number of teams in scope).
 */
/** Group per-day totals by year-month (YYYY-MM), retaining one value per day
 *  in a per-type bucket. */
function groupDailyTotalsByMonth(
  dailyTotals: Map<string, Map<IssueTypeKey, number>>,
): Map<string, Map<IssueTypeKey, number[]>> {
  const byMonth = new Map<string, Map<IssueTypeKey, number[]>>();
  for (const [date, perType] of dailyTotals) {
    const month = date.slice(0, 7);
    let inner = byMonth.get(month);
    if (!inner) {
      inner = new Map();
      byMonth.set(month, inner);
    }
    for (const [type, total] of perType) {
      let bucket = inner.get(type);
      if (!bucket) {
        bucket = [];
        inner.set(type, bucket);
      }
      bucket.push(total);
    }
  }
  return byMonth;
}

/** Average the daily totals within each month, producing the per-type list of
 *  monthly averages to be averaged again by the caller. */
function monthlyAveragesPerType(
  byMonth: Map<string, Map<IssueTypeKey, number[]>>,
): Record<IssueTypeKey, number[]> {
  const monthlyPerType: Record<IssueTypeKey, number[]> = { Story: [], Bug: [], Task: [] };
  for (const inner of byMonth.values()) {
    for (const type of ISSUE_TYPES) {
      const bucket = inner.get(type);
      if (bucket && bucket.length > 0) {
        const sum = bucket.reduce((acc, n) => acc + n, 0);
        monthlyPerType[type].push(sum / bucket.length);
      }
    }
  }
  return monthlyPerType;
}

export function monthlyAverageByType(
  rows: IIssueInProgressDailyTrendRow[],
): IPerTypeNumber {
  if (rows.length === 0) return { Story: 0, Bug: 0, Task: 0 };

  // Step 1: collapse per-team rows into one daily total per (date, type).
  const dailyTotals = sumDailyTotalsByType(rows);
  // Step 2: group daily totals by year-month, retaining one value per day.
  const byMonth = groupDailyTotalsByMonth(dailyTotals);
  if (byMonth.size === 0) return { Story: 0, Bug: 0, Task: 0 };
  // Step 3: average daily totals within each month, then average monthly values.
  const monthlyPerType = monthlyAveragesPerType(byMonth);

  return {
    Story: avgOrZero(monthlyPerType.Story),
    Bug: avgOrZero(monthlyPerType.Bug),
    Task: avgOrZero(monthlyPerType.Task),
  };
}

function avgOrZero(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sum = xs.reduce((acc, n) => acc + n, 0);
  return Math.round(sum / xs.length);
}

/**
 * Convenience: bucket daily WIP rows into ISO weeks AND compute a 4-week
 * rolling average per issue type. Shares the weekly-bucketing helper already
 * used by WipTrendCard so WIP bucketing rules stay consistent across cards.
 */
export function wipWeeklyAverage(
  rows: IIssueInProgressDailyTrendRow[],
  windowWeeks: number,
): { weekly: IWeeklyCounts[]; avg: IPerTypeNumber; wow: Record<IssueTypeKey, IWowDelta> } {
  const weekly = bucketDailyWipToWeekly(rows);
  return {
    weekly,
    avg: rollingWeeklyAverage(weekly, windowWeeks),
    wow: wowDeltasByType(weekly),
  };
}

/** Re-export for callers that want the raw ISO week key helper. */
export { isoWeekStart } from './weekly-bucketing';
